import { FormEvent, useEffect, useRef, useState } from 'react'
import { api } from './api'
import { Notice } from './ui'
import { SystemLayout } from './SystemAdmins'

type Event = { sequence: number; occurred_at: string; label: string; event_type: string; outcome: string; name: string; email: string; company: string; app: string }
type Feed = { items: Event[]; next_before: number }
type Company = { id: string; name: string }

const outcomeText = (outcome: string) => outcome === 'success' ? 'Done' : outcome === 'denied' ? 'Blocked' : outcome === 'failure' || outcome === 'error' ? 'Failed' : outcome

export function activityQuery(filter: string, company: string, search: string) {
  const params = new URLSearchParams({ filter })
  if (company) params.set('company', company)
  if (search) params.set('q', search)
  return params.toString()
}

export function SystemActivity() {
  const [items, setItems] = useState<Event[]>([])
  const [next, setNext] = useState(0)
  const [filter, setFilter] = useState('all')
  const [company, setCompany] = useState('')
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')
  const [companies, setCompanies] = useState<Company[]>([])
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  const query = activityQuery(filter, company, search)
  // Only the newest request may update the list: a slow response for an old
  // filter must never overwrite results for the current one.
  const latest = useRef(0)

  const load = async (older = false) => {
    const id = ++latest.current
    setBusy(true)
    setError('')
    try {
      const data = await api<Feed>(`/api/v1/system/activity?${query}${older ? `&before=${next}` : ''}`, { signal: AbortSignal.timeout(20000) })
      if (id !== latest.current) return
      setItems(previous => older ? [...previous, ...data.items] : data.items)
      setNext(data.next_before)
    } catch {
      if (id === latest.current) setError('We could not load activity. Try again.')
    } finally {
      if (id === latest.current) setBusy(false)
    }
  }
  useEffect(() => { setItems([]); setNext(0); void load() }, [query])
  useEffect(() => {
    api<{ tenants: Company[] }>('/api/v1/operator/overview', { signal: AbortSignal.timeout(20000) })
      .then(data => setCompanies(data.tenants ?? []))
      .catch(() => setCompanies([]))
  }, [])

  const submit = (event: FormEvent) => { event.preventDefault(); setSearch(draft.trim()) }

  return <SystemLayout active="activity">
    <div className="page-heading">
      <div><h1>Activity</h1><p>See sign-ins and changes to accounts. Newest first.</p></div>
      <button className="button secondary" disabled={busy} onClick={() => { void load() }}>Refresh</button>
    </div>
    <section className="section-card">
      <form className="activity-filters" onSubmit={submit}>
        <label>Show<select value={filter} onChange={e => setFilter(e.target.value)}><option value="all">All activity</option><option value="signins">Sign-ins</option><option value="changes">Account changes</option></select></label>
        <label>Company<select value={company} onChange={e => setCompany(e.target.value)}><option value="">All companies</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        <label>Search<input type="search" value={draft} maxLength={100} placeholder="Name, email or event" onChange={e => setDraft(e.target.value)} /></label>
        <div className="activity-filter-actions">
          <button className="button secondary">Search</button>
          <a className="button secondary" href={`/api/v1/system/activity?${query}&format=csv`} download>Export CSV</a>
        </div>
      </form>
      {search && <p className="section-description">Showing results for “{search}”. <button type="button" className="quiet-button" onClick={() => { setDraft(''); setSearch('') }}>Clear search</button></p>}
      {error && <Notice tone="error">{error}</Notice>}
      {busy && <p role="status">Loading activity…</p>}
      {!busy && !error && !items.length && <p>No activity to show.</p>}
      <div className="activity-list">{items.map(item => <article key={item.sequence} className="activity-row">
        <div>
          <strong>{item.label}</strong>
          <p>{item.name || 'Unknown user'}{item.email && ` · ${item.email}`}</p>
          {(item.company || item.app) && <p className="section-description">{[item.company, item.app].filter(Boolean).join(' · ')}</p>}
          <details><summary>Event details</summary><code>{item.event_type}</code></details>
        </div>
        <div>
          <span className="status-pill">{outcomeText(item.outcome)}</span>
          <p><time dateTime={item.occurred_at}>{new Date(item.occurred_at).toLocaleString()}</time></p>
        </div>
      </article>)}</div>
      {error && <button className="button secondary" disabled={busy} onClick={() => { void load() }}>Try again</button>}
      {next > 0 && <button className="button secondary" disabled={busy} onClick={() => { void load(true) }}>Show older activity</button>}
    </section>
  </SystemLayout>
}
